import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './Organization.css';
import { 
  createOrganizationS3, 
  getOrganizationData, 
  updateOrganizationS3, 
  deleteOrganizationS3,
  generateOrganizationId,
  clearOrganizationCache 
} from '../services/organizationS3Service';
import OrganizationSettings from '../components/OrganizationSettings/OrganizationSettings';
// Types
interface HeadcountBreakdown {
  sde: number;
  sdm: number;
  tpm: number;
  pmt: number;
  bie: number;
  srManager: number;
  other: number;
}

interface DirectReport {
  id: string;
  name: string;
  email: string;
  role: string;
  rtb: number;
  targetHC: number;
  headcount: HeadcountBreakdown;
  tier1Service?: number;
  tier2Service?: number;
}

interface Director {
  id: string;
  name: string;
  email: string;
  targetHC: number;
  charter?: string;           // NEW: Director-specific charter
  tenets?: string[];          // NEW: Director-specific tenets  
  keyPriorities?: string[];   // NEW: Director-specific priorities
  directReports: DirectReport[];
}

interface MissionGoal {
  id: string;
  title: string;
  description: string;
  tags: string[];
  status: 'active' | 'inactive';
}

interface Portfolio {
  id: string;
  name: string;
  description?: string;
  asanaProjectId: string;
  inheritConfig: boolean;
  adminConfig?: { primaryAdmin: string; secondaryAdmin?: string };
  agentConfig?: { chat?: string; finaljson?: string; explore?: string; narrative?: string; prioritize?: string };
  asanaConfig?: { personalAccessToken?: string; workspaceId?: string };
  fieldMappings?: any[];
  charter?: string;
  missionGoals?: MissionGoal[];
  keyPriorities?: string[];
  tenets?: string[];
  programs?: string[];
  themes?: string[];
  directors: Director[];
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
}

interface Organization {
  id: string;
  name: string;
  asanaProject?: string;
  charter?: string;
  missionGoals?: MissionGoal[];
  keyPriorities?: string[];
  tenets?: string[];
  directors: Director[];
  // Multi-tenant fields
  adminConfig?: { primaryAdmin: string; secondaryAdmin?: string };
  agentConfig?: any;
  asanaConfig?: any;
  fieldMappings?: any[];
  portfolios?: Portfolio[];
}

// Inline Portfolio Settings component (editable form within portfolio detail view)
const PortfolioSettingsInline: React.FC<{
  portfolio: Portfolio;
  orgId: string;
  onSaved: (updatedPortfolio: Portfolio) => void;
}> = ({ portfolio, orgId, onSaved }) => {
  const [name, setName] = useState(portfolio.name);
  const [description, setDescription] = useState(portfolio.description || '');
  const [asanaProjectId, setAsanaProjectId] = useState(portfolio.asanaProjectId);
  const [inheritConfig, setInheritConfig] = useState(portfolio.inheritConfig);
  const [primaryAdmin, setPrimaryAdmin] = useState(portfolio.adminConfig?.primaryAdmin || '');
  const [secondaryAdmin, setSecondaryAdmin] = useState(portfolio.adminConfig?.secondaryAdmin || '');
  // Custom config state (used when inheritConfig is OFF)
  const [chatAgentId, setChatAgentId] = useState(portfolio.agentConfig?.chat || '');
  const [finaljsonAgentId, setFinaljsonAgentId] = useState(portfolio.agentConfig?.finaljson || '');
  const [customPat, setCustomPat] = useState(portfolio.asanaConfig?.personalAccessToken || '');
  const [customWorkspaceId, setCustomWorkspaceId] = useState(portfolio.asanaConfig?.workspaceId || '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim() || !asanaProjectId.trim()) {
      alert('Portfolio Name and Asana Project ID are required.');
      return;
    }
    setSaving(true);
    setMsg(null);

    // Build agentConfig and asanaConfig only when not inheriting and values are provided
    const agentConfig = !inheritConfig && (chatAgentId.trim() || finaljsonAgentId.trim())
      ? {
          ...(portfolio.agentConfig || {}),
          ...(chatAgentId.trim() ? { chat: chatAgentId.trim() } : {}),
          ...(finaljsonAgentId.trim() ? { finaljson: finaljsonAgentId.trim() } : {}),
        }
      : inheritConfig ? undefined : portfolio.agentConfig;

    const asanaConfig = !inheritConfig && (customPat.trim() || customWorkspaceId.trim())
      ? {
          ...(portfolio.asanaConfig || {}),
          ...(customPat.trim() ? { personalAccessToken: customPat.trim() } : {}),
          ...(customWorkspaceId.trim() ? { workspaceId: customWorkspaceId.trim() } : {}),
        }
      : inheritConfig ? undefined : portfolio.asanaConfig;

    const updated: Portfolio = {
      ...portfolio,
      name,
      description: description || undefined,
      asanaProjectId,
      inheritConfig,
      adminConfig: { primaryAdmin, secondaryAdmin: secondaryAdmin || undefined },
      agentConfig: agentConfig as any,
      asanaConfig: asanaConfig as any,
      updatedAt: new Date().toISOString(),
    };
    try {
      // We need to update the portfolio within the org's portfolios array
      const orgData = await getOrganizationData();
      if (orgData) {
        const org = orgData.organizations.find((o: any) => o.id === orgId);
        if (org && (org as any).portfolios) {
          const updatedPortfolios = ((org as any).portfolios as Portfolio[]).map(p =>
            p.id === portfolio.id ? updated : p
          );
          await updateOrganizationS3(orgId, { portfolios: updatedPortfolios } as any);
          onSaved(updated);
          setMsg('✅ Portfolio settings saved!');
          setTimeout(() => setMsg(null), 3000);
        }
      }
    } catch (err) {
      setMsg(`❌ Failed to save: ${err}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3>⚙️ Portfolio Settings</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {msg && <span style={{ fontSize: '13px' }}>{msg}</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : '💾 Save Settings'}
          </button>
        </div>
      </div>

      <div className="form-group">
        <label>Portfolio Name *</label>
        <input type="text" value={name} onChange={e => setName(e.target.value)} />
      </div>
      <div className="form-group">
        <label>Description</label>
        <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description" />
      </div>
      <div className="form-group">
        <label>Asana Project ID *</label>
        <input type="text" value={asanaProjectId} onChange={e => setAsanaProjectId(e.target.value)} style={{ fontFamily: "'SF Mono', monospace" }} />
      </div>
      <div className="form-group">
        <label>Primary Admin</label>
        <input type="email" value={primaryAdmin} onChange={e => setPrimaryAdmin(e.target.value)} placeholder="admin@amazon.com" />
      </div>
      <div className="form-group">
        <label>Secondary Admin</label>
        <input type="email" value={secondaryAdmin} onChange={e => setSecondaryAdmin(e.target.value)} placeholder="backup@amazon.com" />
      </div>
      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input type="checkbox" checked={inheritConfig} onChange={e => setInheritConfig(e.target.checked)} style={{ width: '18px', height: '18px' }} />
          <span style={{ fontWeight: 600 }}>Inherit organization settings</span>
        </label>
        <p style={{ color: '#718096', fontSize: '13px', marginTop: '4px' }}>
          {inheritConfig
            ? 'Uses org-level agent IDs, Asana PAT, and field mappings.'
            : 'This portfolio uses its own configuration (custom agent IDs, PAT, field mappings).'}
        </p>
      </div>

      {!inheritConfig && (
        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px' }}>
          <h4 style={{ fontSize: '14px', color: '#4a5568', marginBottom: '12px' }}>⚙️ Custom Configuration</h4>
          <p style={{ color: '#718096', fontSize: '13px', marginBottom: '12px' }}>
            Override org-level settings. Leave blank to fall back to org defaults.
          </p>
          <details style={{ marginBottom: '12px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '13px', padding: '8px 0' }}>🤖 Custom Agent IDs</summary>
            <div style={{ padding: '8px 0 8px 16px' }}>
              <div className="form-group">
                <label>Chat Agent ID</label>
                <input type="text" value={chatAgentId} onChange={e => setChatAgentId(e.target.value)} placeholder="Leave blank for org default" style={{ fontFamily: "'SF Mono', monospace", fontSize: '12px' }} />
              </div>
              <div className="form-group">
                <label>FinalJSON Agent ID</label>
                <input type="text" value={finaljsonAgentId} onChange={e => setFinaljsonAgentId(e.target.value)} placeholder="Leave blank for org default" style={{ fontFamily: "'SF Mono', monospace", fontSize: '12px' }} />
              </div>
            </div>
          </details>
          <details style={{ marginBottom: '12px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '13px', padding: '8px 0' }}>🔗 Custom Asana Credentials</summary>
            <div style={{ padding: '8px 0 8px 16px' }}>
              <div className="form-group">
                <label>Personal Access Token</label>
                <input type="password" value={customPat} onChange={e => setCustomPat(e.target.value)} placeholder="Leave blank for org PAT" />
              </div>
              <div className="form-group">
                <label>Workspace ID</label>
                <input type="text" value={customWorkspaceId} onChange={e => setCustomWorkspaceId(e.target.value)} placeholder="Leave blank for org workspace" style={{ fontFamily: "'SF Mono', monospace" }} />
              </div>
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

interface OrganizationProps {}

const Organization: React.FC<OrganizationProps> = () => {
  // Routing hooks
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(orgId || '');
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(true);
  const [showDirectorModal, setShowDirectorModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showOrgStrategyModal, setShowOrgStrategyModal] = useState(false);
  const [showOrgMissionModal, setShowOrgMissionModal] = useState(false);
  const [showOrgPrioritiesModal, setShowOrgPrioritiesModal] = useState(false);
  const [showDirectorStrategyModal, setShowDirectorStrategyModal] = useState(false);
  const [showEditOrgNameModal, setShowEditOrgNameModal] = useState(false);
  const [editingDirector, setEditingDirector] = useState<Director | null>(null);
  const [editingReport, setEditingReport] = useState<DirectReport | null>(null);
  const [editingMissionGoals, setEditingMissionGoals] = useState<MissionGoal[]>([]);
  const [goalTagInputs, setGoalTagInputs] = useState<string[]>([]);
  const [currentDirectorId, setCurrentDirectorId] = useState<string>('');
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [activeTab, setActiveTab] = useState<'portfolios' | 'settings' | 'overview' | 'mission' | 'priorities' | 'directors'>('portfolios');
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingOrgId, setDeletingOrgId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<{id: string; name: string} | null>(null);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [editingPortfolioData, setEditingPortfolioData] = useState<Portfolio | null>(null);
  const [portfolioForm, setPortfolioForm] = useState({
    name: '',
    description: '',
    asanaProjectId: '',
    inheritConfig: true,
  });
  const [showDeletePortfolioConfirm, setShowDeletePortfolioConfirm] = useState(false);
  const [portfolioToDelete, setPortfolioToDelete] = useState<{id: string; name: string} | null>(null);

  const selectedOrg = organizations.find(o => o.id === selectedOrgId);

  // Load data from S3 on component mount
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const orgData = await getOrganizationData();
      if (orgData) {
        setOrganizations(orgData.organizations || []);
        // Use orgId from URL if available, otherwise use saved selectedOrgId
        setSelectedOrgId(orgId || orgData.selectedOrgId || '');
      }
      } catch (error) {
        console.error('Error loading organization data from S3:', error);
        // Fallback to localStorage for existing data
        const savedData = localStorage.getItem('organizationHeadcountData');
        if (savedData) {
          try {
            const parsedData = JSON.parse(savedData);
            setOrganizations(parsedData.organizations || []);
            // Use orgId from URL if available, otherwise use saved selectedOrgId
            setSelectedOrgId(orgId || parsedData.selectedOrgId || '');
          } catch (error) {
            console.error('Error loading organization data from localStorage:', error);
          }
        }
      } finally {
        setIsLoadingOrgs(false);
      }
    };
    
    loadOrganizations();
  }, [orgId]);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const calculateTotalHC = (director: Director) => {
    // Start with 1 for the director himself
    let total = 1;
    
    // Add all direct reports (the managers themselves)
    total += director.directReports.length;
    
    // Add all team members under each direct report
    director.directReports.forEach(dr => {
      total += dr.headcount.sde + dr.headcount.sdm + dr.headcount.tpm + 
               dr.headcount.pmt + dr.headcount.bie + dr.headcount.srManager + dr.headcount.other;
    });
    
    return total;
  };

  const calculateTotalByRole = (director: Director, role: keyof HeadcountBreakdown) => {
    let total = 0;
    
    // Count team members under each direct report
    total += director.directReports.reduce((sum, dr) => sum + dr.headcount[role], 0);
    
    // Also count direct reports themselves if they match the role (excluding Principal roles)
    director.directReports.forEach(dr => {
      if (role === 'srManager' && (dr.role.includes('Sr. Software Development Manager') || dr.role.includes('Sr. Manager'))) {
        total += 1;
      } else if (role === 'sdm' && dr.role.includes('Software Development Manager') && !dr.role.includes('Sr.')) {
        total += 1;
      } else if (role === 'sde' && dr.role.includes('Software Development Engineer') && !dr.role.includes('Principal')) {
        // Exclude Principal SDE from regular SDE count
        total += 1;
      } else if (role === 'pmt' && dr.role.includes('Product Manager') && !dr.role.includes('Principal')) {
        // Exclude Principal PMT from regular PMT count
        total += 1;
      } else if (role === 'tpm' && dr.role.includes('Technical Program Manager') && !dr.role.includes('Principal')) {
        // Exclude Principal TPM from regular TPM count
        total += 1;
      } else if (role === 'bie' && dr.role.includes('BIE')) {
        total += 1;
      }
    });
    
    return total;
  };

  const calculateExecutiveAssistants = (director: Director) => {
    // Count EAs from team members + direct reports who are EAs
    let total = 0;
    total += director.directReports.reduce((sum, dr) => sum + (dr.role.includes('Executive Assistant') ? 1 : 0), 0);
    total += director.directReports.reduce((sum, dr) => sum + dr.headcount.other, 0); // Assuming EAs might be in 'other' category for team members
    return total;
  };

  const calculatePrincipalSDE = (director: Director) => {
    // Count Principal SDEs from direct reports
    return director.directReports.reduce((sum, dr) => sum + (dr.role.includes('Principal Software Development Engineer') ? 1 : 0), 0);
  };

  const calculatePrincipalPMT = (director: Director) => {
    // Count Principal PMTs from direct reports
    return director.directReports.reduce((sum, dr) => sum + (dr.role.includes('Principal Product Manager') ? 1 : 0), 0);
  };

  const calculatePrincipalTPM = (director: Director) => {
    // Count Principal TPMs from direct reports
    return director.directReports.reduce((sum, dr) => sum + (dr.role.includes('Principal Technical Program Manager') ? 1 : 0), 0);
  };

  const calculateOtherRoles = (director: Director) => {
    // Count Other roles from direct reports
    return director.directReports.reduce((sum, dr) => sum + (dr.role === 'Other' ? 1 : 0), 0);
  };

  const calculateTotalRTB = (director: Director) => {
    return director.directReports.reduce((sum, dr) => sum + dr.rtb, 0);
  };

  const calculateTotalTier1Service = (director: Director) => {
    return director.directReports.reduce((sum, dr) => sum + (dr.tier1Service || 0), 0);
  };

  const calculateTotalTier2Service = (director: Director) => {
    return director.directReports.reduce((sum, dr) => sum + (dr.tier2Service || 0), 0);
  };

  const calculateReportTotalHC = (report: DirectReport) => {
    // Only count the team members, not the manager themselves
    return report.headcount.sde + report.headcount.sdm + report.headcount.tpm + 
           report.headcount.pmt + report.headcount.bie + report.headcount.srManager + report.headcount.other;
  };

  // Helper function to calculate organization stats
  const calculateOrgStats = (org: Organization) => {
    const totalDirectors = org.directors.length;
    const totalReports = org.directors.reduce((sum, dir) => sum + dir.directReports.length, 0);
    const totalHC = org.directors.reduce((sum, dir) => sum + calculateTotalHC(dir), 0);
    const totalTargetHC = org.directors.reduce((sum, dir) => sum + dir.targetHC, 0);
    const totalRTB = org.directors.reduce((sum, dir) => sum + calculateTotalRTB(dir), 0);
    const totalSDE = org.directors.reduce((sum, dir) => sum + calculateTotalByRole(dir, 'sde'), 0);
    const totalSDM = org.directors.reduce((sum, dir) => sum + calculateTotalByRole(dir, 'sdm'), 0);
    const totalSrManager = org.directors.reduce((sum, dir) => sum + calculateTotalByRole(dir, 'srManager'), 0);
    const totalPMT = org.directors.reduce((sum, dir) => sum + calculateTotalByRole(dir, 'pmt'), 0);
    const totalTPM = org.directors.reduce((sum, dir) => sum + calculateTotalByRole(dir, 'tpm'), 0);
    const totalBIE = org.directors.reduce((sum, dir) => sum + calculateTotalByRole(dir, 'bie'), 0);
    const totalEA = org.directors.reduce((sum, dir) => sum + calculateExecutiveAssistants(dir), 0);
    const totalOther = org.directors.reduce((sum, dir) => sum + calculateTotalByRole(dir, 'other'), 0);
    const totalPrincipalSDE = org.directors.reduce((sum, dir) => sum + calculatePrincipalSDE(dir), 0);
    const totalPrincipalPMT = org.directors.reduce((sum, dir) => sum + calculatePrincipalPMT(dir), 0);
    const totalPrincipalTPM = org.directors.reduce((sum, dir) => sum + calculatePrincipalTPM(dir), 0);
    const totalOtherRoles = org.directors.reduce((sum, dir) => sum + calculateOtherRoles(dir), 0);
    const totalTier1Service = org.directors.reduce((sum, dir) => sum + calculateTotalTier1Service(dir), 0);
    const totalTier2Service = org.directors.reduce((sum, dir) => sum + calculateTotalTier2Service(dir), 0);
    
    return { 
      totalDirectors, 
      totalReports, 
      totalHC, 
      totalTargetHC, 
      totalRTB,
      totalSDE,
      totalSDM,
      totalSrManager,
      totalPMT,
      totalTPM,
      totalBIE,
      totalEA,
      totalOther,
      totalPrincipalSDE,
      totalPrincipalPMT,
      totalPrincipalTPM,
      totalOtherRoles,
      totalTier1Service,
      totalTier2Service
    };
  };

  // Handle organization selection
  const handleSelectOrg = (selectedOrgId: string) => {
    navigate(`/admin/organization/${selectedOrgId}`);
  };

  // Handle back to list
  const handleBackToList = () => {
    navigate('/');
  };

  const handleCreateOrg = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);
    const newOrg: Organization = {
      id: generateOrganizationId( formData.get('name') as string),
      name: formData.get('name') as string,
      asanaProject: formData.get('asanaProject') as string || undefined,
      directors: []
    };
    
    try {
      // Create organization in S3
      await createOrganizationS3(newOrg);
      
      // Update local state
      const updatedOrganizations = [...organizations, newOrg];
      setOrganizations(updatedOrganizations);
      setShowOrgModal(false);
      // Navigate to the new organization
      navigate(`/admin/organization/${newOrg.id}`);
      
   
    } catch (error) {
      console.error('Error creating organization in S3:', error);
      alert('Failed to create organization. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateDirector = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);
    
    try {
      let updatedOrganizations;
      
      if (selectedPortfolioId) {
        // Portfolio-level: add/edit director inside the selected portfolio
        if (editingDirector) {
          updatedOrganizations = organizations.map(org => {
            if (org.id === selectedOrgId && org.portfolios) {
              return {
                ...org,
                portfolios: (org.portfolios as Portfolio[]).map(p =>
                  p.id === selectedPortfolioId
                    ? {
                        ...p,
                        directors: p.directors.map(d =>
                          d.id === editingDirector.id ? {
                            ...d,
                            name: formData.get('name') as string,
                            email: formData.get('email') as string,
                            targetHC: parseInt(formData.get('targetHC') as string)
                          } : d
                        )
                      }
                    : p
                )
              };
            }
            return org;
          });
          setEditingDirector(null);
        } else {
          const newDirector: Director = {
            id: generateId(),
            name: formData.get('name') as string,
            email: formData.get('email') as string,
            targetHC: parseInt(formData.get('targetHC') as string),
            directReports: []
          };
          
          updatedOrganizations = organizations.map(org => {
            if (org.id === selectedOrgId && org.portfolios) {
              return {
                ...org,
                portfolios: (org.portfolios as Portfolio[]).map(p =>
                  p.id === selectedPortfolioId
                    ? { ...p, directors: [...p.directors, newDirector] }
                    : p
                )
              };
            }
            return org;
          });
        }
      } else {
        // Org-level: add/edit director on the organization itself
        if (editingDirector) {
          updatedOrganizations = organizations.map(org => {
            if (org.id === selectedOrgId) {
              return {
                ...org,
                directors: org.directors.map(d => 
                  d.id === editingDirector.id ? {
                    ...d,
                    name: formData.get('name') as string,
                    email: formData.get('email') as string,
                    targetHC: parseInt(formData.get('targetHC') as string)
                  } : d
                )
              };
            }
            return org;
          });
          setEditingDirector(null);
        } else {
          const newDirector: Director = {
            id: generateId(),
            name: formData.get('name') as string,
            email: formData.get('email') as string,
            targetHC: parseInt(formData.get('targetHC') as string),
            directReports: []
          };
          
          updatedOrganizations = organizations.map(org => 
            org.id === selectedOrgId ? { ...org, directors: [...org.directors, newDirector] } : org
          );
        }
      }
      
      // Update S3 with the modified organization
      const updatedOrg = updatedOrganizations.find(org => org.id === selectedOrgId);
      if (updatedOrg) {
        await updateOrganizationS3(selectedOrgId, updatedOrg);
      }
      
      setOrganizations(updatedOrganizations);
      setShowDirectorModal(false);
      
  
    } catch (error) {
      console.error('Error saving director to S3:', error);
      alert('Failed to save director. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateOrgStrategy = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);
    
    const charter = formData.get('charter') as string;
    const tenetsText = formData.get('tenets') as string;
    const prioritiesText = formData.get('priorities') as string;
    
    const tenets = tenetsText ? tenetsText.split('\n').filter(t => t.trim()) : [];
    const priorities = prioritiesText ? prioritiesText.split('\n').filter(p => p.trim()) : [];
    
    try {
      // Update organization in S3
      await updateOrganizationS3(selectedOrgId, {
        charter,
        tenets,
        keyPriorities: priorities
      });
      
      // Update local state
      setOrganizations(organizations.map(org => 
        org.id === selectedOrgId ? {
          ...org,
          charter,
          tenets,
          keyPriorities: priorities
        } : org
      ));
      
      setShowOrgStrategyModal(false);
   
    } catch (error) {
      console.error('Error updating organization strategy in S3:', error);
      alert('Failed to update organization strategy. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateOrgMission = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);
    
    const charter = formData.get('charter') as string;
    
    // Collect mission goals from individual form fields
    const missionGoals: MissionGoal[] = editingMissionGoals.filter(goal => 
      goal.title.trim() || goal.description.trim()
    );
    
    try {
      if (selectedPortfolioId && selectedOrg?.portfolios) {
        // Save to portfolio level
        const updatedPortfolios = (selectedOrg.portfolios as Portfolio[]).map(p =>
          p.id === selectedPortfolioId
            ? { ...p, charter, missionGoals, updatedAt: new Date().toISOString() }
            : p
        );
        await updateOrganizationS3(selectedOrgId, { portfolios: updatedPortfolios } as any);
        
        // Update local state
        setOrganizations(organizations.map(org => 
          org.id === selectedOrgId ? { ...org, portfolios: updatedPortfolios } : org
        ));
      } else {
        // Legacy: save to org level
        await updateOrganizationS3(selectedOrgId, { charter, missionGoals });
        setOrganizations(organizations.map(org => 
          org.id === selectedOrgId ? { ...org, charter, missionGoals } : org
        ));
      }
      
      setShowOrgMissionModal(false);
      setEditingMissionGoals([]);
 
    } catch (error) {
      console.error('Error updating mission in S3:', error);
      alert('Failed to update mission. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addMissionGoal = () => {
    setEditingMissionGoals([...editingMissionGoals, {
      id: generateId(),
      title: '',
      description: '',
      tags: [],
      status: 'active'
    }]);
    setGoalTagInputs([...goalTagInputs, '']);
  };

  const updateMissionGoal = (index: number, field: keyof MissionGoal, value: string | string[]) => {
    const updatedGoals = [...editingMissionGoals];
    if (field === 'tags') {
      updatedGoals[index][field] = value as string[];
    } else if (field === 'status') {
      updatedGoals[index][field] = value as 'active' | 'inactive';
    } else {
      updatedGoals[index][field] = value as string;
    }
    setEditingMissionGoals(updatedGoals);
  };

  const removeMissionGoal = (index: number) => {
    setEditingMissionGoals(editingMissionGoals.filter((_, i) => i !== index));
    setGoalTagInputs(goalTagInputs.filter((_, i) => i !== index));
  };

  const handleOpenMissionModal = () => {
    // Initialize editing goals from portfolio-level data (activeData) which resolves from selectedPortfolio
    const currentGoals = (selectedPortfolio?.missionGoals || selectedOrg?.missionGoals || []) as MissionGoal[];
    setEditingMissionGoals([...currentGoals]);
    // Initialize tag inputs with current tag values
    setGoalTagInputs(currentGoals.map(goal => goal.tags.join(', ')));
    setShowOrgMissionModal(true);
  };

  const handleUpdateOrgPriorities = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);
    
    const prioritiesText = formData.get('priorities') as string;
    const tenetsText = formData.get('tenets') as string;
    
    const priorities = prioritiesText ? prioritiesText.split('\n').filter(p => p.trim()) : [];
    const tenets = tenetsText ? tenetsText.split('\n').filter(t => t.trim()) : [];
    
    try {
      if (selectedPortfolioId && selectedOrg?.portfolios) {
        // Save to portfolio level
        const updatedPortfolios = (selectedOrg.portfolios as Portfolio[]).map(p =>
          p.id === selectedPortfolioId
            ? { ...p, keyPriorities: priorities, tenets, updatedAt: new Date().toISOString() }
            : p
        );
        await updateOrganizationS3(selectedOrgId, { portfolios: updatedPortfolios } as any);
        
        // Update local state
        setOrganizations(organizations.map(org => 
          org.id === selectedOrgId ? { ...org, portfolios: updatedPortfolios } : org
        ));
      } else {
        // Legacy: save to org level
        await updateOrganizationS3(selectedOrgId, { keyPriorities: priorities, tenets });
        setOrganizations(organizations.map(org => 
          org.id === selectedOrgId ? { ...org, keyPriorities: priorities, tenets } : org
        ));
      }
      
      setShowOrgPrioritiesModal(false);

    } catch (error) {
      console.error('Error updating priorities in S3:', error);
      alert('Failed to update priorities. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateOrgName = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);
    const newName = formData.get('name') as string;
    const asanaProject = formData.get('asanaProject') as string;
    
    try {
      // Update organization in S3
      await updateOrganizationS3(selectedOrgId, {
        name: newName,
        asanaProject: asanaProject || undefined
      });
      
      // Update local state
      setOrganizations(organizations.map(org => 
        org.id === selectedOrgId ? {
          ...org,
          name: newName,
          asanaProject: asanaProject || undefined
        } : org
      ));
      
      setShowEditOrgNameModal(false);

    } catch (error) {
      console.error('Error updating organization name in S3:', error);
      alert('Failed to update organization name. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateReport = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);
    
    const reportData = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      role: formData.get('role') as string,
      rtb: parseFloat(formData.get('rtb') as string) || 0,
      targetHC: parseInt(formData.get('targetHC') as string) || 0,
      headcount: {
        sde: parseInt(formData.get('sde') as string) || 0,
        sdm: parseInt(formData.get('sdm') as string) || 0,
        tpm: parseInt(formData.get('tpm') as string) || 0,
        pmt: parseInt(formData.get('pmt') as string) || 0,
        bie: parseInt(formData.get('bie') as string) || 0,
        srManager: parseInt(formData.get('srManager') as string) || 0,
        other: parseInt(formData.get('other') as string) || 0
      },
      tier1Service: parseInt(formData.get('tier1Service') as string) || 0,
      tier2Service: parseInt(formData.get('tier2Service') as string) || 0
    };

    // Helper to update directors array (works for both org-level and portfolio-level)
    const updateDirectors = (directors: Director[]): Director[] => {
      if (editingReport) {
        return directors.map(d => {
          if (d.id === currentDirectorId) {
            return {
              ...d,
              directReports: d.directReports.map(dr => 
                dr.id === editingReport.id ? { ...dr, ...reportData } : dr
              )
            };
          }
          return d;
        });
      } else {
        const newReport: DirectReport = {
          id: generateId(),
          ...reportData
        };
        return directors.map(d => 
          d.id === currentDirectorId ? { ...d, directReports: [...d.directReports, newReport] } : d
        );
      }
    };

    try {
      let updatedOrganizations;

      if (selectedPortfolioId) {
        // Portfolio-level: update directors inside the selected portfolio
        updatedOrganizations = organizations.map(org => {
          if (org.id === selectedOrgId && org.portfolios) {
            return {
              ...org,
              portfolios: (org.portfolios as Portfolio[]).map(p =>
                p.id === selectedPortfolioId
                  ? { ...p, directors: updateDirectors(p.directors) }
                  : p
              )
            };
          }
          return org;
        });
      } else {
        // Org-level: update directors on the organization itself
        updatedOrganizations = organizations.map(org => {
          if (org.id === selectedOrgId) {
            return {
              ...org,
              directors: updateDirectors(org.directors)
            };
          }
          return org;
        });
      }

      if (editingReport) {
        setEditingReport(null);
      }

      // Update S3 with the modified organization
      const updatedOrg = updatedOrganizations.find(org => org.id === selectedOrgId);
      if (updatedOrg) {
        await updateOrganizationS3(selectedOrgId, updatedOrg);
      }
      
      setOrganizations(updatedOrganizations);
      setShowReportModal(false);
      
    
    } catch (error) {
      console.error('Error saving direct report to S3:', error);
      alert('Failed to save direct report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDirector = async (directorId: string) => {
    if (window.confirm('Are you sure you want to delete this director?')) {
      try {
        let updatedOrganizations;
        
        if (selectedPortfolioId) {
          // Portfolio-level: remove director from the selected portfolio
          updatedOrganizations = organizations.map(org => {
            if (org.id === selectedOrgId && org.portfolios) {
              return {
                ...org,
                portfolios: (org.portfolios as Portfolio[]).map(p =>
                  p.id === selectedPortfolioId
                    ? { ...p, directors: p.directors.filter(d => d.id !== directorId) }
                    : p
                )
              };
            }
            return org;
          });
        } else {
          // Org-level: remove director from the organization
          updatedOrganizations = organizations.map(org => 
            org.id === selectedOrgId ? { ...org, directors: org.directors.filter(d => d.id !== directorId) } : org
          );
        }

        // Update S3 with the modified organization
        const updatedOrg = updatedOrganizations.find(org => org.id === selectedOrgId);
        if (updatedOrg) {
          await updateOrganizationS3(selectedOrgId, updatedOrg);
        }

        setOrganizations(updatedOrganizations);
       
      } catch (error) {
        console.error('Error deleting director from S3:', error);
        alert('Failed to delete director. Please try again.');
      }
    }
  };

  const handleDeleteReport = async (directorId: string, reportId: string) => {
    if (window.confirm('Are you sure you want to delete this direct report?')) {
      try {
        const removeReport = (directors: Director[]): Director[] =>
          directors.map(d => 
            d.id === directorId ? { ...d, directReports: d.directReports.filter(dr => dr.id !== reportId) } : d
          );

        let updatedOrganizations;

        if (selectedPortfolioId) {
          // Portfolio-level: remove report from the selected portfolio's directors
          updatedOrganizations = organizations.map(org => {
            if (org.id === selectedOrgId && org.portfolios) {
              return {
                ...org,
                portfolios: (org.portfolios as Portfolio[]).map(p =>
                  p.id === selectedPortfolioId
                    ? { ...p, directors: removeReport(p.directors) }
                    : p
                )
              };
            }
            return org;
          });
        } else {
          // Org-level: remove report from the organization's directors
          updatedOrganizations = organizations.map(org => {
            if (org.id === selectedOrgId) {
              return {
                ...org,
                directors: removeReport(org.directors)
              };
            }
            return org;
          });
        }

        // Update S3 with the modified organization
        const updatedOrg = updatedOrganizations.find(org => org.id === selectedOrgId);
        if (updatedOrg) {
          await updateOrganizationS3(selectedOrgId, updatedOrg);
        }

        setOrganizations(updatedOrganizations);

      } catch (error) {
        console.error('Error deleting direct report from S3:', error);
        alert('Failed to delete direct report. Please try again.');
      }
    }
  };

  const handleDeleteOrganization = async (organizationId: string) => {

    if (deletingOrgId) {
    
      return;
    }
    
    setDeletingOrgId(organizationId);
    try {

      // Use the organizationS3Service which includes API key authentication
      await deleteOrganizationS3(organizationId);
      
      // Clear cache to ensure other pages get fresh data
      clearOrganizationCache();
      
      // Update local state
      const updatedOrganizations = organizations.filter(org => org.id !== organizationId);
      setOrganizations(updatedOrganizations);
      
      // If we're currently viewing the deleted organization, go back to organization list
      if (selectedOrgId === organizationId) {
        navigate('/');
      }
      

    } catch (error) {
      console.error('Error deleting organization:', error);
      alert(`Failed to delete organization: ${error}`);
    } finally {
      setDeletingOrgId(null);
    }
  };

  const saveToS3 = () => {
    const dataStr = JSON.stringify(organizations, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'org-headcount-data.json';
    link.click();
    URL.revokeObjectURL(url);
    alert('Data exported! In production, this would save to S3.');
  };

  // Render organization list view
  const renderOrganizationList = () => {
    if (isLoadingOrgs) {
      return (
        <div className="loading-state">
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3182ce',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <h3>Loading organizations...</h3>
          <p>Please wait while we fetch your organization data</p>
        </div>
      );
    }
    
    return (
      <>
        {organizations.length === 0 ? (
          <div className="empty-state">
            <h3>No organizations yet</h3>
            <p>Create your first organization to start tracking headcount</p>
          </div>
        ) : (
        <>
          <div className="org-selector">
            <h3>Select an Organization</h3>
            <p>Choose an organization below to view its directors and headcount details</p>
            <select value={selectedOrgId} onChange={(e) => handleSelectOrg(e.target.value)}>
              <option value="">Click here to select an organization...</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>

          <div className="org-cards-grid">
            {organizations.map(org => {
              const stats = calculateOrgStats(org);
              return (
                <div key={org.id} className="org-card" onClick={() => handleSelectOrg(org.id)}>
                  <h3>{org.name}</h3>
                  <p>Click to view directors and headcount details</p>
                  <div className="org-card-stats">
                    <div className="org-card-stat">
                      <span>👥</span>
                      <span>{stats.totalDirectors} Directors</span>
                    </div>
                    <div className="org-card-stat">
                      <span>🏢</span>
                      <span>{stats.totalHC} Total HC</span>
                    </div>
                  </div>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setOrgToDelete({id: org.id, name: org.name});
                      setShowDeleteConfirm(true);
                    }}
                    className="org-delete-btn"
                    title="Delete Organization"
                    disabled={deletingOrgId === org.id}
                  >
                    {deletingOrgId === org.id ? (
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #f56565',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                    ) : '🗑️'}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
    );
  };

  // Get the selected portfolio object
  const selectedPortfolio = selectedPortfolioId && selectedOrg?.portfolios
    ? (selectedOrg.portfolios as Portfolio[]).find(p => p.id === selectedPortfolioId)
    : null;

  // Active data source: when a portfolio is selected, use its data for Overview/Mission/Priorities/Directors
  // This creates a virtual "org" object that the existing render functions can read from
  const activeData = selectedPortfolio ? {
    ...selectedOrg!,
    directors: selectedPortfolio.directors || [],
    charter: selectedPortfolio.charter,
    missionGoals: selectedPortfolio.missionGoals,
    keyPriorities: selectedPortfolio.keyPriorities,
    tenets: selectedPortfolio.tenets,
  } : selectedOrg;

  // Render org-level tab navigation (Portfolios + Settings)
  const renderOrgTabNavigation = () => (
    <div className="tab-navigation">
      <button 
        className={`tab-btn ${activeTab === 'portfolios' ? 'active' : ''}`}
        onClick={() => { setActiveTab('portfolios'); setSelectedPortfolioId(null); }}
      >
        📁 Portfolios
      </button>
      <button 
        className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
        onClick={() => { setActiveTab('settings'); setSelectedPortfolioId(null); }}
      >
        ⚙️ Settings
      </button>
    </div>
  );

  // Render portfolio-level tab navigation (Overview, Mission, Priorities, Directors)
  const renderPortfolioTabNavigation = () => (
    <div className="tab-navigation">
      <button 
        className="tab-btn"
        onClick={() => { setSelectedPortfolioId(null); setActiveTab('portfolios'); }}
        style={{ marginRight: '12px' }}
      >
        ← Back to Portfolios
      </button>
      <button 
        className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
        onClick={() => setActiveTab('overview')}
      >
        📊 Overview
      </button>
      <button 
        className={`tab-btn ${activeTab === 'mission' ? 'active' : ''}`}
        onClick={() => setActiveTab('mission')}
      >
        🎯 Mission
      </button>
      <button 
        className={`tab-btn ${activeTab === 'priorities' ? 'active' : ''}`}
        onClick={() => setActiveTab('priorities')}
      >
        📋 Priorities
      </button>
      <button 
        className={`tab-btn ${activeTab === 'directors' ? 'active' : ''}`}
        onClick={() => setActiveTab('directors')}
      >
        👥 Directors
      </button>
      <button 
        className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
        onClick={() => setActiveTab('settings')}
      >
        ⚙️ Settings
      </button>
    </div>
  );

  // Render portfolio list (clickable cards)
  const renderPortfoliosList = () => {
    if (!selectedOrg) return null;
    const portfolios = (selectedOrg.portfolios || []) as Portfolio[];
    
    return (
      <div className="tab-content">
        <div className="directors-header">
          <h3>📁 Portfolios ({portfolios.length})</h3>
        </div>
        <div className="org-cards-grid">
          {portfolios.length === 0 ? null : (
            portfolios.map(portfolio => (
              <div 
                key={portfolio.id} 
                className="org-card" 
                onClick={() => {
                  setSelectedPortfolioId(portfolio.id);
                  setActiveTab('overview');
                }}
                style={{ cursor: 'pointer' }}
              >
                <h3>{portfolio.name}</h3>
                <p>{portfolio.description || 'Click to view portfolio details'}</p>
                <div className="org-card-stats">
                  <div className="org-card-stat">
                    <span>👥</span>
                    <span>{portfolio.directors?.length || 0} Directors</span>
                  </div>
                  <div className="org-card-stat">
                    <span>{portfolio.inheritConfig ? '🔗' : '⚙️'}</span>
                    <span>{portfolio.inheritConfig ? 'Inherited' : 'Custom'}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-small"
                    onClick={(e) => {
                      e.stopPropagation();
                      const url = `${window.location.origin}/org/${selectedOrgId}/chat/${portfolio.id}`;
                      navigator.clipboard.writeText(url).then(() => alert(`Chatbot link copied!\n${url}`));
                    }}
                    title="Copy shareable chatbot link"
                  >
                    📋 Share Chat Link
                  </button>
                  <button
                    className="btn btn-small"
                    onClick={(e) => {
                      e.stopPropagation();
                      const url = `${window.location.origin}/org/${selectedOrgId}/tasks/${portfolio.id}`;
                      navigator.clipboard.writeText(url).then(() => alert(`Tasks link copied!\n${url}`));
                    }}
                    title="Copy shareable tasks link"
                  >
                    📋 Share Tasks Link
                  </button>
                  <button
                    className="btn btn-primary btn-small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingPortfolioData(portfolio);
                      setPortfolioForm({
                        name: portfolio.name,
                        description: portfolio.description || '',
                        asanaProjectId: portfolio.asanaProjectId,
                        inheritConfig: portfolio.inheritConfig,
                      });
                      setShowPortfolioModal(true);
                    }}
                    title="Edit portfolio"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className="btn btn-danger btn-small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPortfolioToDelete({ id: portfolio.id, name: portfolio.name });
                      setShowDeletePortfolioConfirm(true);
                    }}
                    title="Delete portfolio"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))
          )}
          {/* Create New Portfolio Card — always visible */}
          <div 
            className="org-card" 
            onClick={() => {
              setEditingPortfolioData(null);
              setPortfolioForm({ name: '', description: '', asanaProjectId: '', inheritConfig: true });
              setShowPortfolioModal(true);
            }}
            style={{ 
              cursor: 'pointer', 
              border: '2px dashed #cbd5e0', 
              background: 'transparent',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '140px',
              color: '#a0aec0'
            }}
          >
            <div style={{ fontSize: '36px', fontWeight: 300, lineHeight: 1, marginBottom: '8px' }}>+</div>
            <p style={{ fontWeight: 600, fontSize: '14px', margin: 0 }}>
              {portfolios.length === 0 ? 'Create Your First Portfolio' : 'Add Portfolio'}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Render overview tab content
  const renderOverviewTab = () => {
    if (!activeData) return null;
    
    const stats = calculateOrgStats(activeData);
    
    return (
      <div className="tab-content">
        <div className="overview-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.totalTargetHC}</div>
            <div className="stat-label">Target HC</div>
          </div>
          <div className={`stat-card ${stats.totalHC > stats.totalTargetHC ? 'stat-over' : 'stat-under'}`}>
            <div className="stat-value">{stats.totalHC}</div>
            <div className="stat-label">Actual HC</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.totalRTB}</div>
            <div className="stat-label">RTB HC ({stats.totalHC > 0 ? Math.round((stats.totalRTB / stats.totalHC) * 100) : 0}%)</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.totalDirectors}</div>
            <div className="stat-label">Directors</div>
          </div>
          {stats.totalSrManager > 0 && (
            <div className="stat-card">
              <div className="stat-value">{stats.totalSrManager}</div>
              <div className="stat-label">Sr Managers ({stats.totalTargetHC > 0 ? Math.round((stats.totalSrManager / stats.totalTargetHC) * 100) : 0}%)</div>
            </div>
          )}
          {stats.totalSDM > 0 && (
            <div className="stat-card">
              <div className="stat-value">{stats.totalSDM}</div>
              <div className="stat-label">SDM ({stats.totalTargetHC > 0 ? Math.round((stats.totalSDM / stats.totalTargetHC) * 100) : 0}%)</div>
            </div>
          )}
          {stats.totalSDE > 0 && (
            <div className="stat-card">
              <div className="stat-value">{stats.totalSDE}</div>
              <div className="stat-label">SDE ({stats.totalTargetHC > 0 ? Math.round((stats.totalSDE / stats.totalTargetHC) * 100) : 0}%)</div>
            </div>
          )}
          {stats.totalPMT > 0 && (
            <div className="stat-card">
              <div className="stat-value">{stats.totalPMT}</div>
              <div className="stat-label">PMT ({stats.totalTargetHC > 0 ? Math.round((stats.totalPMT / stats.totalTargetHC) * 100) : 0}%)</div>
            </div>
          )}
          {stats.totalTPM > 0 && (
            <div className="stat-card">
              <div className="stat-value">{stats.totalTPM}</div>
              <div className="stat-label">TPM ({stats.totalTargetHC > 0 ? Math.round((stats.totalTPM / stats.totalTargetHC) * 100) : 0}%)</div>
            </div>
          )}
          {stats.totalBIE > 0 && (
            <div className="stat-card">
              <div className="stat-value">{stats.totalBIE}</div>
              <div className="stat-label">BIE ({stats.totalTargetHC > 0 ? Math.round((stats.totalBIE / stats.totalTargetHC) * 100) : 0}%)</div>
            </div>
          )}
          {stats.totalEA > 0 && (
            <div className="stat-card">
              <div className="stat-value">{stats.totalEA}</div>
              <div className="stat-label">EA ({stats.totalTargetHC > 0 ? Math.round((stats.totalEA / stats.totalTargetHC) * 100) : 0}%)</div>
            </div>
          )}
          {stats.totalPrincipalSDE > 0 && (
            <div className="stat-card">
              <div className="stat-value">{stats.totalPrincipalSDE}</div>
              <div className="stat-label">Principal SDE ({stats.totalTargetHC > 0 ? Math.round((stats.totalPrincipalSDE / stats.totalTargetHC) * 100) : 0}%)</div>
            </div>
          )}
          {stats.totalPrincipalPMT > 0 && (
            <div className="stat-card">
              <div className="stat-value">{stats.totalPrincipalPMT}</div>
              <div className="stat-label">Principal PMT ({stats.totalTargetHC > 0 ? Math.round((stats.totalPrincipalPMT / stats.totalTargetHC) * 100) : 0}%)</div>
            </div>
          )}
          {stats.totalPrincipalTPM > 0 && (
            <div className="stat-card">
              <div className="stat-value">{stats.totalPrincipalTPM}</div>
              <div className="stat-label">Principal TPM ({stats.totalTargetHC > 0 ? Math.round((stats.totalPrincipalTPM / stats.totalTargetHC) * 100) : 0}%)</div>
            </div>
          )}
          {stats.totalOtherRoles > 0 && (
            <div className="stat-card">
              <div className="stat-value">{stats.totalOtherRoles}</div>
              <div className="stat-label">Other Roles ({stats.totalTargetHC > 0 ? Math.round((stats.totalOtherRoles / stats.totalTargetHC) * 100) : 0}%)</div>
            </div>
          )}
          {stats.totalOther > 0 && (
            <div className="stat-card">
              <div className="stat-value">{stats.totalOther}</div>
              <div className="stat-label">Other ({stats.totalTargetHC > 0 ? Math.round((stats.totalOther / stats.totalTargetHC) * 100) : 0}%)</div>
            </div>
          )}
          {stats.totalTier1Service > 0 && (
            <div className="stat-card">
              <div className="stat-value">{stats.totalTier1Service}</div>
              <div className="stat-label">Tier 1 Service</div>
            </div>
          )}
          {stats.totalTier2Service > 0 && (
            <div className="stat-card">
              <div className="stat-value">{stats.totalTier2Service}</div>
              <div className="stat-label">Tier 2 Service </div>
            </div>
          )}
        </div>
        
        {activeData.charter && (
          <div className="strategy-preview">
            <h3>Charter</h3>
            <p>{activeData.charter}</p>
          </div>
        )}
      </div>
    );
  };

  // Render mission tab content
  const renderMissionTab = () => {
    if (!activeData) return null;
    
    return (
      <div className="tab-content">
        <div className="strategy-section">
          <div className="strategy-header">
            <h3>Mission</h3>
            <button 
              className="btn btn-primary btn-small"
              onClick={handleOpenMissionModal}
            >
              Edit Mission
            </button>
          </div>
          
          <div className="strategy-content">
            <div className="strategy-item">
              <h4>Charter</h4>
              <p>{activeData.charter || 'No charter defined yet.'}</p>
            </div>
            
            <div className="strategy-item">
              <h4>Mission Goals</h4>
              {activeData.missionGoals && activeData.missionGoals.length > 0 ? (
                <div className="mission-goals">
                  {activeData.missionGoals.map((goal) => (
                    <div key={goal.id} className={`mission-goal-card ${goal.status === 'inactive' ? 'mission-goal-inactive' : ''}`}>
                      <div className="goal-header">
                        <div className="goal-title-with-status">
                          <h5>{goal.title}</h5>
                          <span className={`goal-status-badge ${goal.status === 'active' ? 'status-active' : 'status-inactive'}`}>
                            {goal.status === 'active' ? '✅ Active' : '⏸️ Inactive'}
                          </span>
                        </div>
                        <div className="goal-tags">
                          {goal.tags.map((tag, index) => (
                            <span key={index} className="goal-tag">{tag}</span>
                          ))}
                        </div>
                      </div>
                      <p>{goal.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No mission goals defined yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render priorities tab content
  const renderPrioritiesTab = () => {
    if (!activeData) return null;
    
    return (
      <div className="tab-content">
        <div className="strategy-section">
          <div className="strategy-header">
            <h3>Priorities</h3>
            <button 
              className="btn btn-primary btn-small"
              onClick={() => setShowOrgPrioritiesModal(true)}
            >
              Edit Priorities
            </button>
          </div>
          
          <div className="strategy-content">
            <div className="strategy-item">
              <h4>Key Priorities</h4>
              {activeData.keyPriorities && activeData.keyPriorities.length > 0 ? (
                <ul>
                  {activeData.keyPriorities.map((priority, index) => (
                    <li key={index}>{priority}</li>
                  ))}
                </ul>
              ) : (
                <p>No key priorities defined yet.</p>
              )}
            </div>
            
            <div className="strategy-item">
              <h4>Tenets</h4>
              {activeData.tenets && activeData.tenets.length > 0 ? (
                <ul>
                  {activeData.tenets.map((tenet, index) => (
                    <li key={index}>{tenet}</li>
                  ))}
                </ul>
              ) : (
                <p>No tenets defined yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render directors tab content
  const renderDirectorsTab = () => {
    if (!activeData) return null;
    
    return (
      <div className="tab-content">
        <div className="directors-header">
          <h3>Directors</h3>
          <button 
            className="btn btn-primary" 
            onClick={() => {
              setEditingDirector(null);
              setShowDirectorModal(true);
            }}
          >
            Add Director
          </button>
        </div>

        {activeData.directors.length === 0 ? (
          <div className="empty-state">
            <h3>No directors yet</h3>
            <p>Add a director to start tracking headcount for {activeData.name}</p>
          </div>
        ) : (
          activeData.directors.map(director => (
            <div key={director.id} className="director-card">
              <div className="director-header">
                <div className="director-info">
                  <h2>{director.name}</h2>
                  <p>{director.email}</p>
                </div>
                <div className="director-actions">
                  <button 
                    className="btn btn-primary btn-small"
                    onClick={() => {
                      setEditingDirector(director);
                      setShowDirectorModal(true);
                    }}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-danger btn-small"
                    onClick={() => handleDeleteDirector(director.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="dashboard-director">
                <div className="dashboard-tile target">
                  <div className="tile-value">{director.targetHC}</div>
                  <div className="tile-label">Target HC</div>
                </div>
                <div className={`dashboard-tile ${(() => {
                  const actualHC = calculateTotalHC(director);
                  const targetHC = director.targetHC;
                  const isOverTarget = actualHC > targetHC;
     
                  return isOverTarget ? 'actual-red' : 'actual-green';
                })()}`}>
                  <div className="tile-value">{calculateTotalHC(director)}</div>
                  <div className="tile-label">Actual HC</div>
                </div>
                <div className="dashboard-tile">
                  <div className="tile-value">{calculateTotalRTB(director)}</div>
                  <div className="tile-label">RTB HC ({calculateTotalHC(director) > 0 ? Math.round((calculateTotalRTB(director) / calculateTotalHC(director)) * 100) : 0}%)</div>
                </div>
                {calculateTotalByRole(director, 'srManager') > 0 && (
                  <div className="dashboard-tile manager">
                    <div className="tile-value">{calculateTotalByRole(director, 'srManager')}</div>
                    <div className="tile-label">Sr Managers/Sr. SDM ({director.targetHC > 0 ? Math.round((calculateTotalByRole(director, 'srManager') / director.targetHC) * 100) : 0}%)</div>
                  </div>
                )}
                {calculateTotalByRole(director, 'sdm') > 0 && (
                  <div className="dashboard-tile">
                    <div className="tile-value">{calculateTotalByRole(director, 'sdm')}</div>
                    <div className="tile-label">SDM ({director.targetHC > 0 ? Math.round((calculateTotalByRole(director, 'sdm') / director.targetHC) * 100) : 0}%)</div>
                  </div>
                )}
                {calculateTotalByRole(director, 'sde') > 0 && (
                  <div className="dashboard-tile">
                    <div className="tile-value">{calculateTotalByRole(director, 'sde')}</div>
                    <div className="tile-label">SDE ({director.targetHC > 0 ? Math.round((calculateTotalByRole(director, 'sde') / director.targetHC) * 100) : 0}%)</div>
                  </div>
                )}
                {calculateTotalByRole(director, 'pmt') > 0 && (
                  <div className="dashboard-tile pmt">
                    <div className="tile-value">{calculateTotalByRole(director, 'pmt')}</div>
                    <div className="tile-label">PMT ({director.targetHC > 0 ? Math.round((calculateTotalByRole(director, 'pmt') / director.targetHC) * 100) : 0}%)</div>
                  </div>
                )}
                {calculateTotalByRole(director, 'tpm') > 0 && (
                  <div className="dashboard-tile tpm">
                    <div className="tile-value">{calculateTotalByRole(director, 'tpm')}</div>
                    <div className="tile-label">TPM ({director.targetHC > 0 ? Math.round((calculateTotalByRole(director, 'tpm') / director.targetHC) * 100) : 0}%)</div>
                  </div>
                )}
                {calculateTotalByRole(director, 'bie') > 0 && (
                  <div className="dashboard-tile bie">
                    <div className="tile-value">{calculateTotalByRole(director, 'bie')}</div>
                    <div className="tile-label">BIE ({director.targetHC > 0 ? Math.round((calculateTotalByRole(director, 'bie') / director.targetHC) * 100) : 0}%)</div>
                  </div>
                )}
                {calculateExecutiveAssistants(director) > 0 && (
                  <div className="dashboard-tile">
                    <div className="tile-value">{calculateExecutiveAssistants(director)}</div>
                    <div className="tile-label">EA ({director.targetHC > 0 ? Math.round((calculateExecutiveAssistants(director) / director.targetHC) * 100) : 0}%)</div>
                  </div>
                )}
                {calculatePrincipalSDE(director) > 0 && (
                  <div className="dashboard-tile">
                    <div className="tile-value">{calculatePrincipalSDE(director)}</div>
                    <div className="tile-label">Principal SDE ({director.targetHC > 0 ? Math.round((calculatePrincipalSDE(director) / director.targetHC) * 100) : 0}%)</div>
                  </div>
                )}
                {calculatePrincipalPMT(director) > 0 && (
                  <div className="dashboard-tile">
                    <div className="tile-value">{calculatePrincipalPMT(director)}</div>
                    <div className="tile-label">Principal PMT ({director.targetHC > 0 ? Math.round((calculatePrincipalPMT(director) / director.targetHC) * 100) : 0}%)</div>
                  </div>
                )}
                {calculatePrincipalTPM(director) > 0 && (
                  <div className="dashboard-tile">
                    <div className="tile-value">{calculatePrincipalTPM(director)}</div>
                    <div className="tile-label">Principal TPM ({director.targetHC > 0 ? Math.round((calculatePrincipalTPM(director) / director.targetHC) * 100) : 0}%)</div>
                  </div>
                )}
                {calculateOtherRoles(director) > 0 && (
                  <div className="dashboard-tile">
                    <div className="tile-value">{calculateOtherRoles(director)}</div>
                    <div className="tile-label">Other Roles ({director.targetHC > 0 ? Math.round((calculateOtherRoles(director) / director.targetHC) * 100) : 0}%)</div>
                  </div>
                )}
                {calculateTotalByRole(director, 'other') > 0 && (
                  <div className="dashboard-tile">
                    <div className="tile-value">{calculateTotalByRole(director, 'other')}</div>
                    <div className="tile-label">Other ({director.targetHC > 0 ? Math.round((calculateTotalByRole(director, 'other') / director.targetHC) * 100) : 0}%)</div>
                  </div>
                )}
              </div>

              <div className="direct-reports">
                <h3>Direct Reports</h3>
                <button 
                  className="btn btn-secondary btn-small"
                  onClick={() => {
                    setCurrentDirectorId(director.id);
                    setEditingReport(null);
                    setShowReportModal(true);
                  }}
                >
                  Add Direct Report
                </button>

                {director.directReports.map(report => (
                  <div key={report.id} className="report-card">
                    <div className="report-header">
                      <div className="report-info">
                        <h4>
                          {report.name}
                          {report.rtb > 0 && <span className="rtb-badge">RTB: {report.rtb}</span>}
                        </h4>
                        <p>{report.email} • {report.role}</p>
                      </div>
                      <div className="report-actions">
                        <button 
                          className="btn btn-primary btn-small"
                          onClick={() => {
                            setCurrentDirectorId(director.id);
                            setEditingReport(report);
                            setShowReportModal(true);
                          }}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn btn-danger btn-small"
                          onClick={() => handleDeleteReport(director.id, report.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="mini-dashboard">
                      <div className="mini-tile">
                        <div className="mini-tile-value">{report.targetHC}</div>
                        <div className="mini-tile-label">Target HC</div>
                      </div>
                      <div className={`mini-tile ${(() => {
                        const actualHC = calculateReportTotalHC(report);
                        const targetHC = report.targetHC;
                        const isOverTarget = actualHC > targetHC;
                  
                        return isOverTarget ? 'actual-red' : 'actual-green';
                      })()}`}>
                        <div className="mini-tile-value">{calculateReportTotalHC(report)}</div>
                        <div className="mini-tile-label">Actual HC</div>
                      </div>
                      {report.rtb > 0 && (
                        <div className="mini-tile">
                          <div className="mini-tile-value">{report.rtb}</div>
                          <div className="mini-tile-label">RTB HC ({calculateReportTotalHC(report) > 0 ? Math.round((report.rtb / calculateReportTotalHC(report)) * 100) : 0}%)</div>
                        </div>
                      )}
                      {report.headcount.srManager > 0 && (
                        <div className="mini-tile">
                          <div className="mini-tile-value">{report.headcount.srManager}</div>
                          <div className="mini-tile-label">Sr Mgr ({report.targetHC > 0 ? Math.round((report.headcount.srManager / report.targetHC) * 100) : 0}%)</div>
                        </div>
                      )}
                      {report.headcount.sdm > 0 && (
                        <div className="mini-tile">
                          <div className="mini-tile-value">{report.headcount.sdm}</div>
                          <div className="mini-tile-label">SDM ({report.targetHC > 0 ? Math.round((report.headcount.sdm / report.targetHC) * 100) : 0}%)</div>
                        </div>
                      )}
                      {report.headcount.sde > 0 && (
                        <div className="mini-tile">
                          <div className="mini-tile-value">{report.headcount.sde}</div>
                          <div className="mini-tile-label">SDE ({report.targetHC > 0 ? Math.round((report.headcount.sde / report.targetHC) * 100) : 0}%)</div>
                        </div>
                      )}
                      {report.headcount.pmt > 0 && (
                        <div className="mini-tile">
                          <div className="mini-tile-value">{report.headcount.pmt}</div>
                          <div className="mini-tile-label">PMT ({report.targetHC > 0 ? Math.round((report.headcount.pmt / report.targetHC) * 100) : 0}%)</div>
                        </div>
                      )}
                      {report.headcount.tpm > 0 && (
                        <div className="mini-tile">
                          <div className="mini-tile-value">{report.headcount.tpm}</div>
                          <div className="mini-tile-label">TPM ({report.targetHC > 0 ? Math.round((report.headcount.tpm / report.targetHC) * 100) : 0}%)</div>
                        </div>
                      )}
                      {report.headcount.bie > 0 && (
                        <div className="mini-tile">
                          <div className="mini-tile-value">{report.headcount.bie}</div>
                          <div className="mini-tile-label">BIE ({report.targetHC > 0 ? Math.round((report.headcount.bie / report.targetHC) * 100) : 0}%)</div>
                        </div>
                      )}
                      {(report.role.includes('Executive Assistant') || report.headcount.other > 0) && (
                        <div className="mini-tile">
                          <div className="mini-tile-value">{report.role.includes('Executive Assistant') ? 1 : 0}</div>
                          <div className="mini-tile-label">EA ({report.targetHC > 0 ? Math.round(((report.role.includes('Executive Assistant') ? 1 : 0) / report.targetHC) * 100) : 0}%)</div>
                        </div>
                      )}
                      {report.headcount.other > 0 && (
                        <div className="mini-tile">
                          <div className="mini-tile-value">{report.headcount.other}</div>
                          <div className="mini-tile-label">Other ({report.targetHC > 0 ? Math.round((report.headcount.other / report.targetHC) * 100) : 0}%)</div>
                        </div>
                      )}
                      {(report.tier1Service && report.tier1Service > 0) && (
                        <div className="mini-tile">
                          <div className="mini-tile-value">{report.tier1Service}</div>
                          <div className="mini-tile-label">Tier 1 Service ({report.targetHC > 0 ? Math.round((report.tier1Service / report.targetHC) * 100) : 0}%)</div>
                        </div>
                      )}
                      {(report.tier2Service && report.tier2Service > 0) && (
                        <div className="mini-tile">
                          <div className="mini-tile-value">{report.tier2Service}</div>
                          <div className="mini-tile-label">Tier 2 Service ({report.targetHC > 0 ? Math.round((report.tier2Service / report.targetHC) * 100) : 0}%)</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  // Render organization detail view
  const renderOrganizationDetail = () => (
    <>

      {/* Show org-level tabs when no portfolio is selected */}
      {!selectedPortfolioId && renderOrgTabNavigation()}
      
      {/* Show portfolio-level tabs when a portfolio is selected */}
      {selectedPortfolioId && selectedPortfolio && renderPortfolioTabNavigation()}

      {/* Org-level content */}
      {!selectedPortfolioId && activeTab === 'portfolios' && renderPortfoliosList()}
      {!selectedPortfolioId && activeTab === 'settings' && selectedOrg && (
        <div className="tab-content">
          <OrganizationSettings
            organization={selectedOrg as any}
            onOrganizationUpdated={(updatedOrg: any) => {
              setOrganizations(organizations.map(org =>
                org.id === updatedOrg.id ? updatedOrg : org
              ));
            }}
          />
        </div>
      )}

      {/* Portfolio-level content (reads from selectedPortfolio data) */}
      {selectedPortfolioId && selectedPortfolio && activeTab === 'overview' && renderOverviewTab()}
      {selectedPortfolioId && selectedPortfolio && activeTab === 'mission' && renderMissionTab()}
      {selectedPortfolioId && selectedPortfolio && activeTab === 'priorities' && renderPrioritiesTab()}
      {selectedPortfolioId && selectedPortfolio && activeTab === 'directors' && renderDirectorsTab()}
      {selectedPortfolioId && selectedPortfolio && activeTab === 'settings' && selectedOrg && (
        <div className="tab-content">
          <PortfolioSettingsInline
            portfolio={selectedPortfolio}
            orgId={selectedOrgId}
            onSaved={(updatedPortfolio) => {
              // Update the portfolio within the org's portfolios array
              const updatedOrgs = organizations.map(org => {
                if (org.id === selectedOrgId && org.portfolios) {
                  return {
                    ...org,
                    portfolios: (org.portfolios as Portfolio[]).map(p =>
                      p.id === updatedPortfolio.id ? updatedPortfolio : p
                    )
                  };
                }
                return org;
              });
              setOrganizations(updatedOrgs);
            }}
          />
        </div>
      )}
    </>
  );

  return (
    <>
      <div className="app-container">
        {orgId ? (
          <div className="header header-compact">
            <div className="breadcrumb-bar">
              <span className="breadcrumb-item breadcrumb-link" onClick={() => navigate('/')}>🐴 HeyFilly</span>
              <span className="breadcrumb-sep">›</span>
              <span className="breadcrumb-item breadcrumb-link" onClick={() => navigate(`/org/${orgId}/hub`)}>{selectedOrg?.name || 'Organization'}</span>
              <span className="breadcrumb-sep">›</span>
              {selectedPortfolio ? (
                <>
                  <span className="breadcrumb-item breadcrumb-link" onClick={() => { setSelectedPortfolioId(null); setActiveTab('portfolios'); }}>Settings</span>
                  <span className="breadcrumb-sep">›</span>
                  <span className="breadcrumb-item breadcrumb-current">{selectedPortfolio.name}</span>
                </>
              ) : (
                <span className="breadcrumb-item breadcrumb-current">
                  {activeTab === 'settings' ? 'Settings' : 'Portfolios'}
                </span>
              )}
            </div>
            <div className="header-actions-right">
              {!selectedPortfolioId && activeTab === 'portfolios' && (
                <button className="btn btn-primary btn-small" onClick={() => {
                  setEditingPortfolioData(null);
                  setPortfolioForm({ name: '', description: '', asanaProjectId: '', inheritConfig: true });
                  setShowPortfolioModal(true);
                }}>
                  + Add Portfolio
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="header">
              <div className="header-content">
                <div className="header-text">
                  <h1>🐴 Organization Manager</h1>
                  <p>Manage your organizations and team structure</p>
                </div>
              </div>
            </div>
            <div className="main-actions">
              <button className="btn btn-primary" onClick={() => setShowOrgModal(true)}>
                + New Organization
              </button>
              <button className="btn btn-secondary" onClick={saveToS3}>
                Export Data
              </button>
            </div>
          </>
        )}

        {!orgId ? renderOrganizationList() : renderOrganizationDetail()}

        {showOrgModal && (
          <div className="modal" onClick={() => setShowOrgModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Create Organization</h3>
                <button className="close-btn" onClick={() => setShowOrgModal(false)}>×</button>
              </div>
              <form onSubmit={handleCreateOrg}>
                <div className="form-group">
                  <label>Organization Name</label>
                  <input type="text" name="name" required />
                </div>
                <div className="form-group">
                  <label>Asana Project</label>
                  <input type="text" name="asanaProject" placeholder="Enter Asana project name or URL..." />
                </div>
                <div className="form-actions">
                  <button type="button" className="btn" onClick={() => setShowOrgModal(false)} disabled={isSubmitting}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid white',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          marginRight: '8px'
                        }}></div>
                        Saving...
                      </>
                    ) : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showDirectorModal && (
          <div className="modal" onClick={() => setShowDirectorModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{editingDirector ? 'Edit Director' : 'Add Director'}</h3>
                <button className="close-btn" onClick={() => setShowDirectorModal(false)}>×</button>
              </div>
              <form onSubmit={handleCreateDirector}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" name="name" required defaultValue={editingDirector?.name} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" required defaultValue={editingDirector?.email} />
                </div>
                <div className="form-group">
                  <label>Target Headcount</label>
                  <input type="number" name="targetHC" required defaultValue={editingDirector?.targetHC} />
                </div>
                <div className="form-actions">
                  <button type="button" className="btn" onClick={() => setShowDirectorModal(false)} disabled={isSubmitting}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid white',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          marginRight: '8px'
                        }}></div>
                        {editingDirector ? 'Updating...' : 'Saving...'}
                      </>
                    ) : (editingDirector ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showReportModal && (
          <div className="modal" onClick={() => setShowReportModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{editingReport ? 'Edit Direct Report' : 'Add Direct Report'}</h3>
                <button className="close-btn" onClick={() => setShowReportModal(false)}>×</button>
              </div>
              <form onSubmit={handleCreateReport}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" name="name" required defaultValue={editingReport?.name} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" required defaultValue={editingReport?.email} />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select name="role" required defaultValue={editingReport?.role}>
                    <option value="">Select Role</option>
                    <option value="Sr. Software Development Manager">Sr. SDM</option>
                    <option value="Sr. Manager">Sr. Manager</option>
                    <option value="Software Development Manager">SDM</option>
                    <option value="Software Development Engineer">SDE</option>
                    <option value="Principal Software Development Engineer">Principal SDE</option>
                    <option value="Product Manager">PMT</option>
                    <option value="Principal Product Manager">Principal PMT</option>
                    <option value="Technical Program Manager">TPM</option>
                    <option value="Principal Technical Program Manager">Principal TPM</option>
                    <option value="BIE">BIE</option>
                    <option value="Executive Assistant">Executive Assistant</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Target Headcount</label>
                  <input type="number" name="targetHC" required defaultValue={editingReport?.targetHC || 0} />
                </div>
                <div className="form-group">
                  <label>Running the Business (RTB) HC</label>
                  <input type="number" step="0.1" name="rtb" defaultValue={editingReport?.rtb || 0} />
                </div>
                <div className="form-group">
                  <label>Team Headcount Breakdown</label>
                  <div className="headcount-grid">
                    <div>
                      <label>SDE</label>
                      <input type="number" name="sde" defaultValue={editingReport?.headcount.sde || 0} />
                    </div>
                    <div>
                      <label>SDM</label>
                      <input type="number" name="sdm" defaultValue={editingReport?.headcount.sdm || 0} />
                    </div>
                    <div>
                      <label>TPM</label>
                      <input type="number" name="tpm" defaultValue={editingReport?.headcount.tpm || 0} />
                    </div>
                    <div>
                      <label>PMT</label>
                      <input type="number" name="pmt" defaultValue={editingReport?.headcount.pmt || 0} />
                    </div>
                    <div>
                      <label>BIE</label>
                      <input type="number" name="bie" defaultValue={editingReport?.headcount.bie || 0} />
                    </div>
                    <div>
                      <label>Sr Manager</label>
                      <input type="number" name="srManager" defaultValue={editingReport?.headcount.srManager || 0} />
                    </div>
                    <div>
                      <label>Other</label>
                      <input type="number" name="other" defaultValue={editingReport?.headcount.other || 0} />
                    </div>
                    <div>
                      <label># Tier 1 Service</label>
                      <input type="number" name="tier1Service" defaultValue={editingReport?.tier1Service || 0} />
                    </div>
                    <div>
                      <label># Tier 2 Service</label>
                      <input type="number" name="tier2Service" defaultValue={editingReport?.tier2Service || 0} />
                    </div>
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" className="btn" onClick={() => setShowReportModal(false)} disabled={isSubmitting}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid white',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          marginRight: '8px'
                        }}></div>
                        {editingReport ? 'Updating...' : 'Saving...'}
                      </>
                    ) : (editingReport ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showOrgStrategyModal && (
          <div className="modal" onClick={() => setShowOrgStrategyModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Edit Organization Strategy</h3>
                <button className="close-btn" onClick={() => setShowOrgStrategyModal(false)}>×</button>
              </div>
              <form onSubmit={handleUpdateOrgStrategy}>
                <div className="form-group">
                  <label>Charter</label>
                  <textarea 
                    name="charter" 
                    rows={4} 
                    placeholder="Enter organization charter..."
                    defaultValue={selectedOrg?.charter || ''}
                  />
                </div>
                <div className="form-group">
                  <label>Tenets (one per line)</label>
                  <textarea 
                    name="tenets" 
                    rows={4} 
                    placeholder="Enter tenets, one per line..."
                    defaultValue={selectedOrg?.tenets?.join('\n') || ''}
                  />
                </div>
                <div className="form-group">
                  <label>Key Priorities (one per line)</label>
                  <textarea 
                    name="priorities" 
                    rows={4} 
                    placeholder="Enter key priorities, one per line..."
                    defaultValue={selectedOrg?.keyPriorities?.join('\n') || ''}
                  />
                </div>
                <div className="form-actions">
                  <button type="button" className="btn" onClick={() => setShowOrgStrategyModal(false)} disabled={isSubmitting}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid white',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          marginRight: '8px'
                        }}></div>
                        Saving...
                      </>
                    ) : 'Save Strategy'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showOrgMissionModal && (
          <div className="modal" onClick={() => setShowOrgMissionModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Edit Organization Mission</h3>
                <button className="close-btn" onClick={() => setShowOrgMissionModal(false)}>×</button>
              </div>
              <form onSubmit={handleUpdateOrgMission}>
                <div className="form-group">
                  <label>Charter</label>
                  <textarea 
                    name="charter" 
                    rows={4} 
                    placeholder="Enter organization charter..."
                    value={selectedOrg?.charter || ''}
                    onChange={(e) => {
                      const charter = e.target.value;
                      setOrganizations(organizations.map(org => 
                        org.id === selectedOrgId ? { ...org, charter } : org
                      ));
                    }}
                  />
                </div>
                
                <div className="form-group">
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
                    <label>Mission Goals</label>
                    <button 
                      type="button" 
                      className="btn btn-secondary btn-small"
                      onClick={addMissionGoal}
                    >
                      Add Goal
                    </button>
                  </div>
                  
                  {editingMissionGoals.length === 0 ? (
                    <p style={{color: '#718096', fontStyle: 'italic'}}>No mission goals yet. Click "Add Goal" to create one.</p>
                  ) : (
                    <div className="mission-goals-editor">
                      {editingMissionGoals.map((goal, index) => (
                        <div key={goal.id} className="mission-goal-editor">
                          <div className="goal-editor-header">
                            <h4>Goal {index + 1}</h4>
                            <button 
                              type="button" 
                              className="btn btn-danger btn-small"
                              onClick={() => removeMissionGoal(index)}
                            >
                              Remove
                            </button>
                          </div>
                          
                          <div className="form-group">
                            <label>Title</label>
                            <input 
                              type="text" 
                              placeholder="Enter goal title..."
                              value={goal.title}
                              onChange={(e) => updateMissionGoal(index, 'title', e.target.value)}
                            />
                          </div>
                          
                          <div className="form-group">
                            <label>Description</label>
                            <textarea 
                              rows={3}
                              placeholder="Enter goal description..."
                              value={goal.description}
                              onChange={(e) => updateMissionGoal(index, 'description', e.target.value)}
                            />
                          </div>
                          
                          <div className="form-group">
                            <label>Tags (comma-separated)</label>
                            <input 
                              type="text" 
                              placeholder="Enter tags separated by commas..."
                              value={goalTagInputs[index] || ''}
                              onChange={(e) => {
                                const newTagInputs = [...goalTagInputs];
                                newTagInputs[index] = e.target.value;
                                setGoalTagInputs(newTagInputs);
                              }}
                              onBlur={(e) => {
                                const inputValue = e.target.value;
                                const tags = inputValue ? inputValue.split(',').map(tag => tag.trim()).filter(tag => tag) : [];
                                updateMissionGoal(index, 'tags', tags);
                              }}
                            />
                          </div>
                          
                          <div className="form-group">
                            <label>Status</label>
                            <select 
                              value={goal.status}
                              onChange={(e) => updateMissionGoal(index, 'status', e.target.value)}
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="form-actions">
                  <button type="button" className="btn" onClick={() => setShowOrgMissionModal(false)} disabled={isSubmitting}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid white',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          marginRight: '8px'
                        }}></div>
                        Saving...
                      </>
                    ) : 'Save Mission'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showOrgPrioritiesModal && (
          <div className="modal" onClick={() => setShowOrgPrioritiesModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Edit Organization Priorities</h3>
                <button className="close-btn" onClick={() => setShowOrgPrioritiesModal(false)}>×</button>
              </div>
              <form onSubmit={handleUpdateOrgPriorities}>
                <div className="form-group">
                  <label>Key Priorities (one per line)</label>
                  <textarea 
                    name="priorities" 
                    rows={4} 
                    placeholder="Enter key priorities, one per line..."
                    defaultValue={(selectedPortfolio?.keyPriorities || selectedOrg?.keyPriorities || []).join('\n')}
                  />
                </div>
                <div className="form-group">
                  <label>Tenets (one per line)</label>
                  <textarea 
                    name="tenets" 
                    rows={4} 
                    placeholder="Enter tenets, one per line..."
                    defaultValue={(selectedPortfolio?.tenets || selectedOrg?.tenets || []).join('\n')}
                  />
                </div>
                <div className="form-actions">
                  <button type="button" className="btn" onClick={() => setShowOrgPrioritiesModal(false)} disabled={isSubmitting}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid white',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          marginRight: '8px'
                        }}></div>
                        Saving...
                      </>
                    ) : 'Save Priorities'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showEditOrgNameModal && (
          <div className="modal" onClick={() => setShowEditOrgNameModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Edit Organization Name</h3>
                <button className="close-btn" onClick={() => setShowEditOrgNameModal(false)}>×</button>
              </div>
              <form onSubmit={handleUpdateOrgName}>
              <div className="form-group">
                  <label>Organization Name</label>
                  <input 
                    type="text" 
                    name="name" 
                    required 
                    defaultValue={selectedOrg?.name || ''}
                    placeholder="Enter organization name..."
                  />
                </div>
                <div className="form-actions">
                  <button type="button" className="btn" onClick={() => setShowEditOrgNameModal(false)} disabled={isSubmitting}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid white',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          marginRight: '8px'
                        }}></div>
                        Saving...
                      </>
                    ) : 'Save Name'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Portfolio Create/Edit Modal */}
        {showPortfolioModal && (
          <div className="modal" onClick={() => setShowPortfolioModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{editingPortfolioData ? 'Edit Portfolio' : 'Create Portfolio'}</h3>
                <button className="close-btn" onClick={() => setShowPortfolioModal(false)}>×</button>
              </div>
              <div className="form-group">
                <label>Portfolio Name *</label>
                <input
                  type="text"
                  value={portfolioForm.name}
                  onChange={(e) => setPortfolioForm({ ...portfolioForm, name: e.target.value })}
                  placeholder="e.g., AFT Outbound"
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={portfolioForm.description}
                  onChange={(e) => setPortfolioForm({ ...portfolioForm, description: e.target.value })}
                  placeholder="Brief description of this portfolio"
                />
              </div>
              <div className="form-group">
                <label>Asana Project ID *</label>
                <input
                  type="text"
                  value={portfolioForm.asanaProjectId}
                  onChange={(e) => setPortfolioForm({ ...portfolioForm, asanaProjectId: e.target.value })}
                  placeholder="1234567890123"
                  required
                  style={{ fontFamily: "'SF Mono', monospace" }}
                />
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={portfolioForm.inheritConfig}
                    onChange={(e) => setPortfolioForm({ ...portfolioForm, inheritConfig: e.target.checked })}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span style={{ fontWeight: 600 }}>Inherit organization settings</span>
                </label>
              </div>
              <div className="form-actions">
                <button type="button" className="btn" onClick={() => setShowPortfolioModal(false)} disabled={isSubmitting}>Cancel</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                  onClick={async () => {
                    if (!portfolioForm.name.trim() || !portfolioForm.asanaProjectId.trim()) {
                      alert('Portfolio Name and Asana Project ID are required.');
                      return;
                    }
                    setIsSubmitting(true);
                    const now = new Date().toISOString();
                    const currentPortfolios = ((selectedOrg?.portfolios || []) as Portfolio[]);
                    let updatedPortfolios: Portfolio[];

                    if (editingPortfolioData) {
                      updatedPortfolios = currentPortfolios.map((p) =>
                        p.id === editingPortfolioData.id
                          ? { ...p, name: portfolioForm.name, description: portfolioForm.description || undefined, asanaProjectId: portfolioForm.asanaProjectId, inheritConfig: portfolioForm.inheritConfig, updatedAt: now }
                          : p
                      );
                    } else {
                      const newPortfolio: Portfolio = {
                        id: `portfolio-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
                        name: portfolioForm.name,
                        description: portfolioForm.description || undefined,
                        asanaProjectId: portfolioForm.asanaProjectId,
                        inheritConfig: portfolioForm.inheritConfig,
                        directors: [],
                        createdAt: now,
                        updatedAt: now,
                      };
                      updatedPortfolios = [...currentPortfolios, newPortfolio];
                    }

                    try {
                      await updateOrganizationS3(selectedOrgId, { portfolios: updatedPortfolios } as any);
                      setOrganizations(organizations.map(org =>
                        org.id === selectedOrgId ? { ...org, portfolios: updatedPortfolios } : org
                      ));
                      setShowPortfolioModal(false);
                      setEditingPortfolioData(null);
                    } catch (err) {
                      alert(`Failed to save portfolio: ${err}`);
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                >
                  {isSubmitting ? 'Saving...' : (editingPortfolioData ? 'Save Changes' : 'Create Portfolio')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Portfolio Confirmation Modal */}
        {showDeletePortfolioConfirm && portfolioToDelete && (
          <div className="modal" onClick={() => setShowDeletePortfolioConfirm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Delete Portfolio</h3>
                <button className="close-btn" onClick={() => setShowDeletePortfolioConfirm(false)}>×</button>
              </div>
              <div className="form-group">
                <p>Are you sure you want to delete <strong>"{portfolioToDelete.name}"</strong>?</p>
                <p style={{color: '#f56565', fontSize: '14px'}}>This action cannot be undone and will permanently remove all portfolio data including directors, mission, and priorities.</p>
              </div>
              <div className="form-actions">
                <button type="button" className="btn" onClick={() => setShowDeletePortfolioConfirm(false)}>Cancel</button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={async () => {
                    const currentPortfolios = ((selectedOrg?.portfolios || []) as Portfolio[]);
                    const updatedPortfolios = currentPortfolios.filter(p => p.id !== portfolioToDelete.id);
                    try {
                      await updateOrganizationS3(selectedOrgId, { portfolios: updatedPortfolios } as any);
                      setOrganizations(organizations.map(org =>
                        org.id === selectedOrgId ? { ...org, portfolios: updatedPortfolios } : org
                      ));
                    } catch (err) {
                      alert(`Failed to delete portfolio: ${err}`);
                    }
                    setShowDeletePortfolioConfirm(false);
                    setPortfolioToDelete(null);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && orgToDelete && (
          <div className="modal" onClick={() => setShowDeleteConfirm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Delete Organization</h3>
                <button className="close-btn" onClick={() => setShowDeleteConfirm(false)}>×</button>
              </div>
              <div className="form-group">
                <p>Are you sure you want to delete <strong>"{orgToDelete.name}"</strong>?</p>
                <p style={{color: '#f56565', fontSize: '14px'}}>This action cannot be undone and will permanently remove all organization data.</p>
              </div>
              <div className="form-actions">
                <button type="button" className="btn" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={() => {
                    handleDeleteOrganization(orgToDelete.id);
                    setShowDeleteConfirm(false);
                    setOrgToDelete(null);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Organization;
